import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface SlaViolationRow {
  readonly violationId: string;
  readonly slaName: string;
  readonly entityReference: string;
  readonly breachedBy: string;
  readonly severity: string;
  readonly breachedAt: string;
}

interface SlaDashboard {
  readonly violations?: readonly SlaViolationRow[];
  readonly data?: { readonly violations?: readonly SlaViolationRow[] };
  readonly error?: string;
}

/** L16.06 — SLA violations (`/sla/violations`). Read-only breach list. */
export function M16SlaViolationsPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<SlaViolationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<SlaDashboard>('/v1/m16/dashboards/sla', { token })
      .then((r) => {
        if (!alive) return;
        if (r?.error) { setError(r.error); return; }
        const violations = r?.violations ?? r?.data?.violations ?? [];
        setRows([...violations]);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m16.sla.loading')}</p>;
  return (
    <main aria-label="sla-violations">
      <h1>{t('m16.sla.title')}</h1>
      <table aria-label="sla-violations-table">
        <thead>
          <tr><th>{t('m16.sla.colSla')}</th><th>{t('m16.sla.colEntity')}</th><th>{t('m16.sla.colBreachedBy')}</th><th>{t('m16.sla.colSeverity')}</th><th>{t('m16.sla.colBreachedAt')}</th></tr>
        </thead>
        <tbody>
          {rows.map((v) => (
            <tr key={v.violationId}>
              <td>{v.slaName}</td>
              <td>{v.entityReference}</td>
              <td>{v.breachedBy}</td>
              <td>{v.severity}</td>
              <td>{at(v.breachedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
